/**
 * 共享对话框：选人（JSSDK chooseContact）+ 勾选自定义分组 → 提交。
 *
 * 提交后展示 ok / failed 两列表，让用户知道谁没收到通知卡（但共享关系已写入）。
 */

import { useEffect, useState } from "react";

import { ApiError } from "@/api/client";
import type { Group } from "@/api/groups";
import { listGroups } from "@/api/groups";
import { shareFavorite, type ShareResult } from "@/api/shares";
import { toast } from "@/components/Toast";
import { chooseContact, type PickedContact } from "@/feishu/jssdk";

interface Props {
  favoriteId: number;
  onClose: () => void;
}

export function ShareDialog({ favoriteId, onClose }: Props) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number>>(new Set());
  const [picked, setPicked] = useState<PickedContact[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ShareResult | null>(null);

  useEffect(() => {
    listGroups()
      .then(setGroups)
      .catch(() => {
        /* 无分组不是致命错误 */
      });
  }, []);

  const toggleGroup = (id: number) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pickFromFeishu = async () => {
    const contacts = await chooseContact({ multi: true, limit: 50 });
    if (contacts.length === 0) {
      toast.info("未选择任何联系人，或非飞书 webview 环境");
      return;
    }
    // 去重合并
    setPicked((prev) => {
      const map = new Map(prev.map((p) => [p.open_id, p]));
      for (const c of contacts) map.set(c.open_id, c);
      return [...map.values()];
    });
  };

  const removePicked = (open_id: string) => {
    setPicked((prev) => prev.filter((p) => p.open_id !== open_id));
  };

  const canSubmit =
    !submitting && (picked.length > 0 || selectedGroupIds.size > 0);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await shareFavorite(favoriteId, {
        users: picked.map((p) => p.open_id),
        group_ids: [...selectedGroupIds],
      });
      setResult(r);
      if (r.failed.length === 0) {
        toast.success(`已共享给 ${r.ok.length} 人`);
      } else {
        toast.info(
          `共享给 ${r.ok.length} 人成功，${r.failed.length} 人通知未送达`,
        );
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "共享失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">共享收藏</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100"
            aria-label="关闭"
          >
            ✕
          </button>
        </div>

        {result ? (
          <ResultView result={result} onClose={onClose} />
        ) : (
          <>
            <section className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-medium text-slate-700">
                  选择同事
                </div>
                <button
                  onClick={pickFromFeishu}
                  className="rounded-full bg-sky-600 px-3 py-1 text-xs text-white hover:bg-sky-700"
                >
                  从通讯录选择
                </button>
              </div>
              {picked.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-400">
                  还没有选人；点上面按钮从飞书通讯录选
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {picked.map((p) => (
                    <span
                      key={p.open_id}
                      className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-xs text-sky-700"
                    >
                      {p.name ?? p.open_id}
                      <button
                        onClick={() => removePicked(p.open_id)}
                        className="text-sky-500 hover:text-sky-800"
                        aria-label="移除"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </section>

            <section className="mb-4">
              <div className="mb-2 text-sm font-medium text-slate-700">分组</div>
              {groups.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-400">
                  还没有分组；先到「分组管理」创建
                </div>
              ) : (
                <div className="space-y-1">
                  {groups.map((g) => (
                    <label
                      key={g.id}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedGroupIds.has(g.id)}
                        onChange={() => toggleGroup(g.id)}
                      />
                      <span className="font-medium text-slate-800">{g.name}</span>
                      <span className="text-xs text-slate-400">
                        {g.members.length} 人
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </section>

            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
              >
                取消
              </button>
              <button
                onClick={submit}
                disabled={!canSubmit}
                className={`rounded-full px-4 py-2 text-sm text-white ${
                  canSubmit
                    ? "bg-sky-600 hover:bg-sky-700"
                    : "cursor-not-allowed bg-slate-300"
                }`}
              >
                {submitting ? "共享中…" : "共享"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ResultView({
  result,
  onClose,
}: {
  result: ShareResult;
  onClose: () => void;
}) {
  return (
    <div className="space-y-4 text-sm">
      <div>
        <div className="mb-1 font-medium text-emerald-700">
          已共享 {result.ok.length} 人
        </div>
        <div className="rounded-lg bg-emerald-50 p-2 text-xs text-emerald-700">
          {result.ok.length === 0 ? "—" : result.ok.join(", ")}
        </div>
      </div>
      {result.failed.length > 0 ? (
        <div>
          <div className="mb-1 font-medium text-rose-700">
            通知未送达 {result.failed.length} 人（共享关系已保留）
          </div>
          <ul className="max-h-40 overflow-y-auto rounded-lg bg-rose-50 p-2 text-xs text-rose-700">
            {result.failed.map((f) => (
              <li key={f.open_id}>
                <strong>{f.open_id}</strong>：{f.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="rounded-full bg-slate-800 px-4 py-2 text-sm text-white"
        >
          完成
        </button>
      </div>
    </div>
  );
}
